import { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { DatesProvider } from "dates/DatesContext";
import moment from "moment";
import BreadcrumbDates, { type Breadcrumb } from "components/core/blocks/BreadcrumbDates";
import useCategories from "hooks/useCategories";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "utils/dashboardLinks";
import { useModuleEnabled } from "redux/selectors/modules";
import checkTierAccess from "utils/checkTierAccess";
import PaymentDiscovery from "components/custom/onboarding/subscription/PaymentDiscovery";
import EmptyState from "components/custom/merchandise/EmptyState";
import { useDates } from "dates/DatesContext";
import { useAppSelector } from "../../redux/hooks";
import PricingTables from "modules/pricingv2/components/PricingTables";
import { PageContainer } from "../../components/core/basic/PageContainer";
import { css } from "../../../styled-system/css";

const getDashboardTo = (category: string | null = null) => {
  return category ? `/user/_pricing/management/category/${category}` : `/user/_pricing/management`;
};

const Dashboard = () => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const { category } = useParams<{ category?: string }>();
  const hasAgencyDashboardModule = useModuleEnabled("AGENCY_DASHBOARD");

  const { currentCategoryDetails: categoryDetails, categories: categoryData } = useCategories(
    category,
    "_pricing/management",
  );

  const { group, start, end, preStart, preEnd } = useDates();

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    empty: true,
    group_by: "category_id",
    category_id: undefined as string | undefined,
    category: undefined as string | undefined,
  };

  if (category) {
    filters["group_by"] = "plan_id";
    filters["category_id"] = category;
    filters["category"] = category;
  }

  const breadCrumbItems: Breadcrumb[] = [
    {
      name: "Pricing Dashboard",
      href: getDashboardTo(),
      dropdownLinkOptions: DASHBOARD_LINKS,
      unsorted: true,
      searchable: false,
      dropdownSubtitle: "Dashboards",
    },
  ];
  if (category) {
    breadCrumbItems.push({
      name: categoryDetails.leaf_category_name ?? "Unknown category",
      href: categoryDetails.href,
      dropdownLinkOptions: categoryData,
      searchable: true,
      searchPlaceholderText: "Search categories...",
      dropdownSubtitle: "Categories",
    });
  }

  if (hasAgencyDashboardModule) {
    breadCrumbItems.unshift(AGENCY_DASHBOARD_BREADCRUMB_SPEC);
  }

  const showEmpty = useMemo(() => {
    const totalSalesStart = user.total_sales_start_date?.[marketPlace.marketPlace];
    if (totalSalesStart) {
      if (moment(totalSalesStart).isBefore(moment().subtract(2, "days"))) {
        return false;
      } else {
        return "COLLECTING_DATA";
      }
    }
    return "NO_DATA";
  }, [user, marketPlace.marketPlace]);

  if (showEmpty) {
    return <EmptyState type={showEmpty} />;
  }

  return (
    <>
      <BreadcrumbDates items={breadCrumbItems} />
      <PageContainer withBreadcrumb padding={"large"}>
        <h3 className={css({ margin: 0 })}>Dynamic pricing management</h3>
        <PricingTables marketPlace={marketPlace} category={category} filters={filters} />
      </PageContainer>
    </>
  );
};

const PricingManagement = () => {
  const hasPriceOptimizationModule = useModuleEnabled("PRICE_OPTIMIZATION");

  const user = useAppSelector((state) => state.user);
  const amz_onboard = useAppSelector((state) => state.amz_onboard);

  const location = useLocation<{ firstVisit?: boolean; onboardingComplete?: boolean } | undefined>();

  const DYNAMIC_PRICING_SUBSCRIPTION =
    (((user.pricing_tier && user.pricing_tier !== "unknown") ||
      location.state?.firstVisit ||
      location.state?.onboardingComplete) &&
      (amz_onboard.org_status === "active" ||
        amz_onboard.org_status === "trial_active" ||
        amz_onboard.org_status === "demo_org")) ||
    hasPriceOptimizationModule ||
    checkTierAccess(user.tier, "pricing", amz_onboard.org_status);

  if (!DYNAMIC_PRICING_SUBSCRIPTION) {
    return <PaymentDiscovery discoveryType={"advertising"} dashboardType="pricing" />;
  }

  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export default PricingManagement;
